// SearchableSelect Component - Custom searchable dropdown similar to select2

import { useState, useRef, useEffect } from "react";

const SearchableSelect = ({
  name,
  value,
  onChange,
  options = [],
  placeholder = "เลือก...",
  label,
  icon,
  className = "",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Get display text for selected value
  const getDisplayText = () => {
    if (!value) return placeholder;
    const selected = options.find((opt) => opt?.value === value);
    return selected?.label || value || placeholder;
  };

  // Filter options based on search term (with safety checks)
  const filteredOptions = options.filter((option) =>
    option?.label?.toLowerCase?.()?.includes(searchTerm.toLowerCase()) ?? false
  );

  // Handle option selection
  const handleSelect = (optionValue) => {
    onChange({
      target: {
        name,
        value: optionValue,
      },
    });
    setSearchTerm("");
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <div className={`relative group ${className}`} ref={dropdownRef}>
      {label && (
        <label className="text-sm font-bold text-gray-700 mb-2.5 flex items-center">
          {icon && (
            <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-purple-600 rounded-lg flex items-center justify-center mr-2 shadow-md group-hover:scale-110 transition-transform">
              {icon}
            </div>
          )}
          {label}
        </label>
      )}

      {/* Selected Value Display / Trigger */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`
          w-full px-4 py-3 border-2 rounded-xl transition-all duration-200
          cursor-pointer flex items-center justify-between
          ${disabled ? "bg-gray-100 cursor-not-allowed" : "bg-gray-50 hover:border-purple-300"}
          ${isOpen ? "border-purple-500 ring-4 ring-purple-200 bg-white" : "border-gray-200"}
        `}
      >
        <span
          className={`${value ? "text-gray-900" : "text-gray-400"} truncate`}
        >
          {getDisplayText()}
        </span>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-2 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border-2 border-purple-300 rounded-xl shadow-2xl overflow-hidden animate-slideDown">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="ค้นหา..."
                className="w-full px-4 py-2 pl-10 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none transition-all"
              />
              <svg
                className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-64 overflow-y-auto">
            {/* Filtered Options */}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={`
                    px-4 py-3 cursor-pointer transition-all duration-150
                    flex items-center gap-3 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50
                    ${
                      value === option.value
                        ? "bg-purple-100 text-purple-700 font-semibold"
                        : "text-gray-700"
                    }
                  `}
                >
                  {value === option.value && (
                    <svg
                      className="w-5 h-5 text-purple-600 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  <span className={value === option.value ? "" : "ml-8"}>
                    {option.label}
                  </span>
                </div>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-gray-400">
                <svg
                  className="w-12 h-12 mx-auto mb-2 opacity-50"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm">ไม่พบข้อมูล</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
